/**
 * El botón que abre el chat con el mensaje ya escrito.
 *
 * Muestra el número ANTES de abrir nada, igual que el remito: el teléfono
 * argentino con el 15 es ambiguo y la heurística puede errarle. Mandarle el
 * presupuesto de un cliente a un desconocido por un dígito de más no se arregla
 * con más heurística, se arregla mostrando el número.
 *
 * Sin teléfono el botón no se dibuja: un botón que no puede funcionar es peor
 * que ninguno.
 */
import { MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { linkWhatsapp, normalizarTelefonoAr } from "@/lib/remito";

export interface BotonWhatsappProps {
  telefono: string | null | undefined;
  texto: string;
  etiqueta: string;
  variante?: "default" | "outline" | "ghost";
  className?: string;
}

function tituloDe(tel: NonNullable<ReturnType<typeof normalizarTelefonoAr>>): string {
  return tel.confianza === "alta"
    ? `Se abre el chat con ${tel.legible}`
    : `Ojo: ${tel.legible} no tiene la forma de un celular argentino. Revisalo antes de mandar.`;
}

export function BotonWhatsapp({
  telefono,
  texto,
  etiqueta,
  variante = "outline",
  className,
}: BotonWhatsappProps): JSX.Element | null {
  const tel = normalizarTelefonoAr(telefono);
  if (!tel) return null;

  return (
    <Button asChild type="button" variant={variante} className={className} title={tituloDe(tel)}>
      <a href={linkWhatsapp(tel.e164, texto)} target="_blank" rel="noopener noreferrer">
        <MessageCircle aria-hidden="true" />
        {etiqueta}
        {tel.confianza === "baja" ? (
          <span className="text-xs text-warning">· revisá el número</span>
        ) : null}
      </a>
    </Button>
  );
}

/**
 * La misma accion como item de un menu "Mas" (la ficha de la orden). Es un
 * <a> adentro del item de Radix: el menu se cierra al elegirlo y el navegador
 * abre el chat en otra pestaña, como con el boton.
 */
export function ItemWhatsapp({
  telefono,
  texto,
  etiqueta,
}: Pick<BotonWhatsappProps, "telefono" | "texto" | "etiqueta">): JSX.Element | null {
  const tel = normalizarTelefonoAr(telefono);
  if (!tel) return null;

  return (
    <DropdownMenuItem asChild title={tituloDe(tel)}>
      <a href={linkWhatsapp(tel.e164, texto)} target="_blank" rel="noopener noreferrer">
        <MessageCircle className="size-4" aria-hidden="true" />
        {etiqueta}
        {tel.confianza === "baja" ? (
          <span className="text-xs text-warning">· revisá el número</span>
        ) : null}
      </a>
    </DropdownMenuItem>
  );
}
